/**
 * WooCommerce / CSV ile birebir alt kategori adları (`SUBCATEGORY_LABEL_TR`, filtreler).
 * Food mega menü sırası `FOOD_MEGA_MENU_ORDER`; benzersiz liste `FOOD_SUBCATEGORIES`.
 */

export const TOP_LEVEL_CATEGORY_KEYS = [
  'Food & Beverages',
  'Construction & Building',
  'Household Cleaning',
  'Personal Care',
];

/**
 * Bu alt kategori değerlerinden birine sahip ürünler, ürün listesinde "Confectionery"
 * filtresi seçildiğinde de gösterilir; CSV `subcategory` alanı değişmez.
 * Mega menü sırası `FOOD_MEGA_MENU_ORDER` (3 sütun, satır satır); tekrarlayan satırlar aynı filtre anahtarını tekrarlar.
 * @see backend/utils/confectioneryGroup.js — aynı liste
 */
export const SUBCATEGORIES_ALSO_IN_CONFECTIONERY = [
  'Confectionery',
  'Biscuit & Cookies',
  'Candy',
  'Chocolate',
  'Chips & Corn Flakes',
  'Chewing Gum',
];

/** Backend `CONFECTIONERY_EXTRA_ALIASES` ile aynı (Woo / CSV varyantları). */
export const CONFECTIONERY_EXTRA_ALIASES = ['Biscuits', 'Cookies', 'Snacks', 'Sweets'];

/**
 * Mega menü "Sunflower Oil" / "Olive Oil":
 * WooCommerce bazen tüm sıvı yağları `subcategory: Edible Oils` tutar — filtre bunu `name` / `name_tr` ile böler:
 * Sunflower Oil → yalnızca Sunflower Oil; Olive Oil → Olive Oil, Extra Virgin Olive Oil, Pomace Olive Oil.
 * @see backend/utils/confectioneryGroup.js — Mongo ile aynı.
 *
 * Arama kutusu: aşağıdaki tam eşleşmelerde Confectionery geniş filtresi (Candy, Chocolate, …).
 * Backend `searchMeansConfectioneryCatalogFilter` ile aynı anlamda tutun.
 */
export const CONFECTIONERY_SEARCH_ALIASES = [
  'confectionery',
  'şekerleme',
  'sekerleme',
  'confiserie',
  'confiteria',
  'confitería',
];

export function isConfectionerySearchQuery(raw) {
  const rawStr = String(raw ?? '').trim();
  if (!rawStr) return false;
  const t = rawStr
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  if (!t) return false;
  const foldedAliases = CONFECTIONERY_SEARCH_ALIASES.map((a) =>
    a
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  );
  if (foldedAliases.includes(t)) return true;
  if (t.length <= 64 && /confectionery/i.test(rawStr)) return true;
  if (/(şekerleme|sekerleme)/i.test(rawStr)) return true;
  return false;
}

function foldCatalogTitle(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** @returns {string[]} lowercase folded `name` + `name_tr` (birincil ticari başlıklar) */
export function foldedProductDisplayTitles(product) {
  if (!product) return [];
  const pairs = [
    foldCatalogTitle(product.name),
    foldCatalogTitle(product.name_tr),
  ].filter(Boolean);
  return [...new Set(pairs)];
}

const OLIVE_OIL_MENU_NAME_FOLD = new Set(['olive oil', 'extra virgin olive oil', 'pomace olive oil']);

/** Eski WooCommerce alt kategori değerleri → yeni menü/filtre anahtarı. */
export const SUBCATEGORY_LEGACY_ALIASES = {
  'Sanitary Napkin': ['Sanitery Napkin'],
  'Dishwasher Rinse Aid': ['Dish Brightener'],
  'Baby Wet Wipes': ['Wet Baby Towel'],
  'Napkins & Facial Tissues': ['Napkin - Facial Tissue'],
  'Iced Coffee': ['Ice Coffee'],
};

function productSubcategoryMatchesFilterValue(productSub, filterSub) {
  const subL = (productSub || '').trim().toLowerCase();
  const filterL = (filterSub || '').trim().toLowerCase();
  if (!filterL) return true;
  if (subL === filterL) return true;
  const legacy = SUBCATEGORY_LEGACY_ALIASES[filterSub?.trim()];
  return legacy?.some((alias) => alias.toLowerCase() === subL) ?? false;
}

export function productMatchesSubcategoryFilter(productSubcategory, filterSubcategory, product) {
  const sub = (productSubcategory || '').trim();
  const filter = (filterSubcategory || '').trim();
  if (!filter) return true;
  const subL = sub.toLowerCase();
  if (filter === 'Confectionery') {
    const labels = [...SUBCATEGORIES_ALSO_IN_CONFECTIONERY, ...CONFECTIONERY_EXTRA_ALIASES];
    return labels.some((x) => x.toLowerCase() === subL);
  }

  if (filter === 'Sunflower Oil') {
    if (subL === 'sunflower oil') return true;
    if (subL !== 'edible oils') return false;
    return foldedProductDisplayTitles(product).some((t) => t === 'sunflower oil');
  }

  if (filter === 'Olive Oil') {
    if (subL === 'olive oil') return true;
    if (subL !== 'edible oils') return false;
    return foldedProductDisplayTitles(product).some((t) => OLIVE_OIL_MENU_NAME_FOLD.has(t));
  }

  if (filter === 'Fruit Juices') {
    return productMatchesFruitJuicesFilter(product);
  }

  return productSubcategoryMatchesFilterValue(sub, filter);
}

/**
 * Gıda mega menü — 3 sütunlu grid; doldurma sırası satır satır (CSS `grid` row-major).
 *
 * Satırlar (soldan sağa):
 * 1. Pasta · Wheat Flour · Sunflower Oil
 * 2. Dried Fruits · Bulgur · Semolina
 * 3. Noodle · Olive Oil · Roasted Red Pepper
 * 4. Roasted Eggplant · Stuffed Grape Leaves · Jams
 * 5. Grape Leaves · Tomato Paste · Pickles
 * 6. Fruit Juices · Confectionery · Salt
 * 7. Energy Drink · Iced Coffee · Mineral Water
 */
export const FOOD_MEGA_MENU_ORDER = [
  'Pasta',
  'Wheat Flour',
  'Sunflower Oil',
  'Dried Fruits',
  'Bulgur',
  'Semolina',
  'Noodle',
  'Olive Oil',
  'Roasted Red Pepper',
  'Roasted Eggplant',
  'Stuffed Grape Leaves',
  'Jams',
  'Grape Leaves',
  'Tomato Paste',
  'Pickles',
  'Fruit Juices',
  'Confectionery',
  'Salt',
  'Energy Drink',
  'Iced Coffee',
  'Mineral Water',
];

/**
 * Ürün listesi (/products) — gıda blok sırası (mega menü gridinden bağımsız).
 * Makarna → un → bulgur → … → maden suyu; temizlik / kişisel bakım kendi listelerinde kalır.
 */
export const FOOD_PRODUCT_GRID_ORDER = [
  'Pasta',
  'Wheat Flour',
  'Bulgur',
  'Semolina',
  'Noodle',
  'Sunflower Oil',
  'Olive Oil',
  'Dried Fruits',
  'Roasted Red Pepper',
  'Roasted Eggplant',
  'Stuffed Grape Leaves',
  'Grape Leaves',
  'Tomato Paste',
  'Pickles',
  'Jams',
  'Confectionery',
  'Salt',
  'Energy Drink',
  'Fruit Juices',
  'Iced Coffee',
  'Mineral Water',
];

const FRUIT_JUICE_GRID_SUB_ALIASES = new Set([
  '100% juice',
  'nectar',
  'fruit drink',
  'organic juice',
]);

/** Meyve suları listesinde üstte gösterilecek ürün adları (sıralı). */
export const FRUIT_JUICE_PRIORITY_NAMES = [
  'Apricot Juice',
  'Pomegranate Juice',
  'Organic Apple Juice',
  'Organic Grape Juice',
  'Organic Orange Juice',
  'Organic Pomegranate Juice',
  'Organic Red Beet Juice',
  'Organic Red Fruit Juice',
  'Organic Tart Cherry Juice',
];

const FRUIT_JUICE_PRIORITY_INDEX = new Map(
  FRUIT_JUICE_PRIORITY_NAMES.map((name, index) => [foldCatalogTitle(name), index]),
);

export function fruitJuiceNameSortIndex(product) {
  const key = foldCatalogTitle(product?.name);
  if (FRUIT_JUICE_PRIORITY_INDEX.has(key)) {
    return FRUIT_JUICE_PRIORITY_INDEX.get(key);
  }
  return FRUIT_JUICE_PRIORITY_NAMES.length;
}

export function isFruitJuiceCatalogProduct(product) {
  return productMatchesFruitJuicesFilter(product);
}

function productMatchesFruitJuicesFilter(product) {
  const subL = (product.subcategory || '').trim().toLowerCase();
  if (subL === 'fruit juices') return true;
  if (FRUIT_JUICE_GRID_SUB_ALIASES.has(subL)) return true;
  return fruitJuiceNameSortIndex(product) < FRUIT_JUICE_PRIORITY_NAMES.length;
}

/** `FOOD_PRODUCT_GRID_ORDER` için ürün satırı eşlemesi (Edible Oils, %100 Juice vb.). */
export function productMatchesFoodGridGroup(product, filterKey) {
  const filter = (filterKey || '').trim();
  if (!filter || !product) return false;
  if (filter === 'Fruit Juices') {
    return productMatchesFruitJuicesFilter(product);
  }
  if (productMatchesSubcategoryFilter(product.subcategory, filter, product)) {
    return true;
  }
  const subL = (product.subcategory || '').trim().toLowerCase();
  if (filter === 'Olive Oil' && subL === 'edible oils') {
    return !foldedProductDisplayTitles(product).some((t) => t === 'sunflower oil');
  }
  return false;
}

/** Benzersiz alt kategori anahtarları (CSV / filtre dokümantasyonu). */
export const FOOD_SUBCATEGORIES = [...new Set(FOOD_MEGA_MENU_ORDER.filter(Boolean))];

export const CONSTRUCTION_SUBCATEGORIES = [
  'Cement',
  'Ceramic & Porcelain Tile',
  'Marble',
  'Paint & Coating',
];

export const CLEANING_SUBCATEGORIES = [
  'Dishwasher Rinse Aid',
  'Dishwashing Detergent',
  'Dishwashing Tablet',
  'Drain Opener',
  'Fabric Softener',
  'Glass Cleaner',
  'Laundry Detergent',
  'Liquid Soap',
  'Rust & Lime Remover',
  'Surface Cleaner',
];

export const PERSONAL_CARE_SUBCATEGORIES = [
  'Baby Diaper',
  'Handkerchief - Pocket Tissue',
  'Napkins & Facial Tissues',
  'Paper Towel',
  'Sanitary Napkin',
  'Toilet Paper',
  'Baby Wet Wipes',
  'Wet Wipes',
];

/** Türkçe mega menü / gösterim (CSV alanı İngilizce kalır) */
export const SUBCATEGORY_LABEL_TR = {
  '100% Juice': '%100 meyve suyu',
  'Biscuit & Cookies': 'Bisküvi ve kurabiye',
  Biscuits: 'Bisküvi',
  Bulgur: 'Bulgur',
  Candy: 'Şekerleme',
  'Chips & Corn Flakes': 'Cips ve mısır gevreği',
  'Chewing Gum': 'Sakız',
  Chocolate: 'Çikolata',
  Confectionery: 'Şekerleme',
  Cookies: 'Kurabiye',
  'Dried Fruits': 'Kuru meyve',
  'Edible Oils': 'Sıvı yağ',
  'Energy Drink': 'Enerji içeceği',
  'Fresh Vegetables & Fruits': 'Taze sebze ve meyve',
  'Fruit Drink': 'Meyveli içecek',
  'Fruit Juices': 'Meyve suları',
  'Grape Leaves': 'Asma yaprağı',
  'Iced Coffee': 'Buzlu kahve',
  'Ice Coffee': 'Buzlu kahve',
  'Olive Oil': 'Zeytinyağı',
  Jams: 'Reçel',
  'Mineral Water': 'Maden suyu',
  Nectar: 'Nektar',
  Noodle: 'Erişte',
  'Organic Juice': 'Organik meyve suyu',
  Pasta: 'Makarna',
  Pickles: 'Turşu',
  'Roasted Eggplant': 'Közlenmiş patlıcan',
  'Roasted Red Pepper': 'Közlenmiş biber',
  Salt: 'Tuz',
  Semolina: 'İrmik',
  Snacks: 'Atıştırmalık',
  Sweets: 'Tatlı ve şekerleme',
  'Stuffed Grape Leaves': 'Yaprak sarma',
  'Sunflower Oil': 'Ayçiçek yağı',
  'Tomato Paste': 'Salça',
  'Wheat Flour': 'Un',
  Cement: 'Çimento',
  'Ceramic & Porcelain Tile': 'Seramik ve porselen karo',
  Marble: 'Mermer',
  'Paint & Coating': 'Boya ve kaplama',
  'Dishwasher Rinse Aid': 'Bulaşık makinesi parlatıcısı',
  'Dish Brightener': 'Bulaşık makinesi parlatıcısı',
  'Dishwashing Detergent': 'Bulaşık deterjanı',
  'Dishwashing Tablet': 'Bulaşık tableti',
  'Drain Opener': 'Gider açıcı',
  'Fabric Softener': 'Yumuşatıcı',
  'Glass Cleaner': 'Cam temizleyici',
  'Laundry Detergent': 'Çamaşır deterjanı',
  'Liquid Soap': 'Sıvı sabun',
  'Rust & Lime Remover': 'Pas ve kireç çözücü',
  'Surface Cleaner': 'Yüzey temizleyici',
  'Baby Diaper': 'Bebek bezi',
  'Handkerchief - Pocket Tissue': 'Cep mendili',
  'Napkins & Facial Tissues': 'Peçete ve yüz havlusu',
  'Napkin - Facial Tissue': 'Peçete ve yüz havlusu',
  'Paper Towel': 'Kağıt havlu',
  'Sanitary Napkin': 'Hijyenik ped',
  'Sanitery Napkin': 'Hijyenik ped',
  'Toilet Paper': 'Tuvalet kağıdı',
  'Baby Wet Wipes': 'Bebek ıslak mendili',
  'Wet Baby Towel': 'Bebek ıslak mendili',
  'Wet Wipes': 'Islak mendil',
  Uncategorized: 'Kategorisiz',
};
